import { inject, Injectable } from '@angular/core';
import {
    PlayAudit,
    PlayResult,
    PlayResultCompletionStatus,
    PlayResultDetail,
    postSalesforceRequestPlayAuditStatuses,
    executionStartPlayAuditStatuses,
} from '@r1-compass-library/models';
import { map, Observable, of } from 'rxjs';
import { PlayErrorService } from './play-error.service';

/**
 * Service for determining the completion status of a play execution.
 */
@Injectable({
    providedIn: 'root',
})
export class PlayCompletionStatusService {
    readonly #playErrorService = inject(PlayErrorService);

    public determineCompletionStatus(
        playResult: PlayResult,
        playAudits: PlayAudit[],
        createDetail?: PlayResultDetail,
        updateDetail?: PlayResultDetail
    ): Observable<PlayResultCompletionStatus> {
        // We need to check the status of the latest play-audit to determine the last known stage of processing for this execution
        const auditsSorted = playAudits.sort((a, b) => (String(b.createdAt) > String(a.createdAt) ? 1 : -1)); // sort audits from latest to earliest
        const latestAudit = auditsSorted.at(0);

        if (latestAudit && postSalesforceRequestPlayAuditStatuses.includes(latestAudit.status)) {
            // If the execution was last audited in any stage after the SF requests were made...
            return this.getStatusForExecutionAfterSalesforce(createDetail, updateDetail);
        } else if (latestAudit && executionStartPlayAuditStatuses.includes(latestAudit.status)) {
            // ... or if we're at the start of the execution before detail records were created
            return of('In Progress');
        } else {
            // ... otherwise we're somewhere in processing but before Salesforce
            return this.getStatusForExecutionBeforeSalesforce(playResult, createDetail, updateDetail);
        }
    }

    private getStatusForExecutionAfterSalesforce(
        createDetail?: PlayResultDetail,
        updateDetail?: PlayResultDetail
    ): Observable<PlayResultCompletionStatus> {
        // CASE: There is a missing detail result, which means we didn't capture data as expected
        if (!createDetail && !updateDetail) {
            return of('Not Available');
        }

        // CASE: We are still waiting for one or more completion requests from the SF integrator
        if (this.hasRecordsToProcess(createDetail) || this.hasRecordsToProcess(updateDetail)) {
            return of('In Progress');
        }

        // CASE: We've received all completion requests and we need to compare counts to determine status
        const totalRecordsSuccessful =
            (createDetail?.numRecordsSuccessful ?? 0) + (updateDetail?.numRecordsSuccessful ?? 0);
        const totalRecordsNonSuccessful =
            (createDetail?.numRecordsFailed ?? 0) +
            (updateDetail?.numRecordsFailed ?? 0) +
            (createDetail?.numRecordsRejected ?? 0) +
            (updateDetail?.numRecordsRejected ?? 0);
        if (totalRecordsNonSuccessful === 0) {
            return of('Complete');
        } else if (totalRecordsSuccessful === 0) {
            return of('Failure');
        } else {
            return of('Partial Success');
        }
    }

    private getStatusForExecutionBeforeSalesforce(
        playResult: PlayResult,
        createDetail?: PlayResultDetail,
        updateDetail?: PlayResultDetail
    ): Observable<PlayResultCompletionStatus> {
        return this.#playErrorService.getErrorDetails({ playResultId: playResult.id }).pipe(
            map(errors => {
                if (errors.length > 0) {
                    if (
                        (!!createDetail || !!updateDetail) && // If we have any result details...
                        (createDetail?.numRecordsSelected ?? 0) === 0 && // ... but no results were eligible for processing...
                        (updateDetail?.numRecordsSelected ?? 0) === 0
                    ) {
                        // CASE: There was a fatal error, but there were no records eligible for processing
                        return 'Complete';
                    } else {
                        // CASE: There was a fatal error
                        return 'Failure';
                    }
                } else if (createDetail === undefined && updateDetail === undefined) {
                    // CASE: No details were created yet - execution is in early stages
                    return 'In Progress';
                } else if (this.hasRecordsToProcess(createDetail) || this.hasRecordsToProcess(updateDetail)) {
                    // CASE: There are records selected for processing, but they are not processed yet
                    return 'In Progress';
                } else if ((createDetail?.numRecordsSelected ?? 0) + (updateDetail?.numRecordsSelected ?? 0) === 0) {
                    // CASE: No records were selected for processing
                    // ! NOTE: There is a small chance of this case being a false positive of 'Complete' when it is actually 'In Progress'.
                    // This can happen if the status is calculated in the small window between the creation of the 'create' details
                    // and creation of the 'update' details.
                    return 'Complete';
                } else {
                    // CASE: Any other case (shouldn't get here)
                    console.warn(`default execution status calculated for play result ${playResult.id}`);
                    return 'In Progress';
                }
            })
        );
    }

    private normalizeCounts(detail: PlayResultDetail): PlayResultDetail {
        // helper method to deal with nulls
        return {
            ...detail,
            numRecordsFailed: detail.numRecordsFailed ? detail.numRecordsFailed : 0,
            numRecordsRejected: detail.numRecordsRejected ? detail.numRecordsRejected : 0,
            numRecordsSelected: detail.numRecordsSelected ? detail.numRecordsSelected : 0,
            numRecordsSuccessful: detail.numRecordsSuccessful ? detail.numRecordsSuccessful : 0,
        } as PlayResultDetail;
    }

    private hasRecordsToProcess(detail?: PlayResultDetail): boolean {
        if (detail !== undefined) {
            detail = this.normalizeCounts(detail);
            if (
                detail.numRecordsSelected > 0 &&
                detail.numRecordsSelected >
                    detail.numRecordsFailed + detail.numRecordsRejected + detail.numRecordsSuccessful
            ) {
                return true; // There are records selected for processing, but SF is not done processing them
            }
        }
        return false; // No records eligible for processing, or SF processed them all
    }
}
